'use client'

import { useRef, type CSSProperties } from 'react'
import {
  findFishingSceneAnchor,
  validateMicroLifePlacementForKind,
} from '@/lib/fishing-world-anchors'
import type { FishingWorldActiveEvent } from '@/lib/fishing-world-scheduler'
import {
  fishingMicroLifePresentationByEventKind,
  fishingMicroLifeSpriteManifest,
} from '@/lib/fishing-world-sprite-manifest'
import { useAmbientEventCompletion } from './ambient-rendering'
import styles from './FishingScene.module.css'

interface AmbientMicroLifeProps {
  events: readonly FishingWorldActiveEvent[]
  onComplete?: (eventId: string) => void
}

function MicroLifeSprite({ event, onComplete }: { event: FishingWorldActiveEvent, onComplete?: (eventId: string) => void }) {
  useAmbientEventCompletion(event, onComplete)
  const initialElapsedMs = useRef(
    Math.min(
      Math.max(1, event.expiresAtMs - event.startedAtMs),
      Math.max(0, Date.now() - event.startedAtMs),
    ),
  ).current
  const presentationId = fishingMicroLifePresentationByEventKind[event.kind]
  const placement = event.presentation?.placement
  if (!presentationId || !placement || validateMicroLifePlacementForKind(event.kind, placement) !== null) return null

  const sprite = fishingMicroLifeSpriteManifest[presentationId]
  const durationMs = Math.max(1, event.expiresAtMs - event.startedAtMs)
  const anchorId = findFishingSceneAnchor(placement.center, placement.allowedAnchors)
  const style = {
    '--micro-x': `${placement.center.x * 100}%`,
    '--micro-y': `${placement.center.y * 100}%`,
    '--micro-scale': placement.spriteScale,
    '--micro-duration': `${durationMs}ms`,
    // Freeze the CSS timeline at mount. Recomputing this from Date.now() on
    // every scheduler render makes otherwise smooth transforms visibly jump.
    '--micro-delay': `${-initialElapsedMs}ms`,
    '--micro-anchor-y': `${sprite.anchor[1] * -100}%`,
    '--micro-atlas': `url('${sprite.atlas}')`,
    '--micro-exit-atlas': `url('${'exitAtlas' in sprite ? sprite.exitAtlas : sprite.atlas}')`,
    '--micro-background-width': `${sprite.frameCount * 100}%`,
  } as CSSProperties

  return (
    <div
      className={styles.microLifeEvent}
      data-testid={`ambient-${event.kind}`}
      data-event-id={event.id}
      data-started-at-ms={event.startedAtMs}
      data-kind={event.kind}
      data-presentation={presentationId}
      data-placement-id={placement.id}
      data-anchor-id={anchorId ?? undefined}
      data-zone-ids={placement.allowedZones.join(' ')}
      data-duration-ms={durationMs}
      data-approach-ms={sprite.lifecycleMs.approach}
      data-rest-ms={sprite.lifecycleMs.rest}
      data-exit-ms={sprite.lifecycleMs.exit}
      data-approach-frame-ms={sprite.poseTimingMs.approachFrame}
      data-rest-cycle-ms={sprite.poseTimingMs.restCycle}
      data-exit-frame-ms={sprite.poseTimingMs.exitFrame}
      style={style}
      aria-hidden="true"
    >
      <span className={styles.microLifeMotion}>
        <i className={styles.microLifeSprite} data-phase="approach" />
        <i className={styles.microLifeSprite} data-phase="rest" />
        <i className={styles.microLifeSprite} data-phase="exit" />
      </span>
    </div>
  )
}

export function AmbientMicroLife({ events, onComplete }: AmbientMicroLifeProps) {
  const controlledEvents = events.filter((event) => fishingMicroLifePresentationByEventKind[event.kind] !== null)
  if (controlledEvents.length === 0) return null
  return (
    <div className={styles.microLifeLayer} aria-hidden="true">
      {controlledEvents.map((event) => <MicroLifeSprite key={event.id} event={event} onComplete={onComplete} />)}
    </div>
  )
}
